import { observer } from 'mobx-react';
import { makeAutoObservable } from 'mobx';
import { IEntityColumn, IEntityToken, ITemplateTrigger, IUserConfigPackage } from '../../../API';
import ReusableDialog, { IReusableDialogFooterProps, ReusableDialogState } from '../../controls/ReusableDialog';
import { InfoTooltipLabel } from '../../controls/InfoTooltipLabel';
import { Link, MessageBar, MessageBarButton, MessageBarType, Stack, TextField, Toggle } from '@fluentui/react';
import { useEffect } from 'react';
import ExpressionField, { ExpressionFieldState } from '../expression-builder/ExpressionField';
import TriggerActions, { TriggerActionsState } from '../trigger-actions/TriggerActions';
import { ExpressionBuilderOutputType } from '../expression-builder/ExpressionBuilder';

export class TemplateTriggerEditorState implements ITemplateTrigger {
	public userConfig: IUserConfigPackage;

	public dialog:ReusableDialogState;

	public Id: string = '';

	public saveClearTemplateMessage: boolean = false;
	
	public TemplateUrl: string = '';
	public TriggerExpression: string = '';
	public CachedTemplateFile: string = '';

	public TemplateProvisionIncludeSharePointIfTeams: boolean = false;
	public TemplateProvisionIncludeFiles: boolean = false;
	public TemplateProvisionIncludeListItems: boolean = false;
	public TemplateApprovalListItemId:string = '';

	public OnCompletionTriggerActionIds: string[] = [];
	public expressionFieldState:ExpressionFieldState;
	public listUrl: string = '';
	//Unused
	//-------------
	public DisplayTitle: string = '';
	public ServerRelativeUrl: string = '';
	public NativeMetadataUrl: string = '';
	original: ITemplateTrigger = {} as ITemplateTrigger;

	completionTriggerActions: TriggerActionsState;
	//-------------

	
	public templateApprovalsListUrl:string = '';


	private thisItem() : ITemplateTrigger {
		return {
			Id: this.Id,
			TemplateUrl: this.TemplateUrl,
			TriggerExpression: this.TriggerExpression,
			CachedTemplateFile: this.CachedTemplateFile,
			TemplateProvisionIncludeSharePointIfTeams: this.TemplateProvisionIncludeSharePointIfTeams,
			TemplateProvisionIncludeFiles: this.TemplateProvisionIncludeFiles,
			TemplateProvisionIncludeListItems: this.TemplateProvisionIncludeListItems,
			DisplayTitle: this.DisplayTitle,
			ServerRelativeUrl: this.ServerRelativeUrl,
			NativeMetadataUrl: this.NativeMetadataUrl,
			OnCompletionTriggerActionIds: this.OnCompletionTriggerActionIds,
			TemplateApprovalListItemId: this.TemplateApprovalListItemId
		};
	}

	clearTemplate(): void {
		this.CachedTemplateFile = '';
		this.saveClearTemplateMessage = true;
	}

	isTeamsOrChannelUrl() {

		if(!this.TemplateUrl) return false;

		return this.TemplateUrl.includes('teams.microsoft.com') || this.TemplateUrl.includes('teams.cloud.microsoft') || 
				(this.TemplateUrl.length === 36 && this.TemplateUrl.indexOf('-') > 0); //Is it a GUID?
	}
	

	public dialogButtons: IReusableDialogFooterProps = {
		leftButtons: [/*
			{
				text: 'Delete',
				iconProps: {iconName: 'Delete'},
				onClick: (ev) => {
					this.dialog.onDismiss();
					this.onDelete(this.original);
				},
			}
		*/],
		rightButtons: [
			{
				text: 'Cancel',
				onClick: (ev) => {
					this.dialog.onDismiss();
				},
			},
			{
				text: 'Save',
				onClick: (ev) => {
					this.dialog.onDismiss();
					this.onSave(this.original, this.thisItem());
				},
			}
		],
	};

	public areTokensLoading: boolean = false;
	public tokens?: IEntityToken[];
	onSave: (original:ITemplateTrigger, updated:ITemplateTrigger) => void;
	onDelete: (profile:ITemplateTrigger) => void;

	constructor(
		userConfig: IUserConfigPackage,
		onDelete: (profile:ITemplateTrigger) => void,
		onSave: (original:ITemplateTrigger, updated:ITemplateTrigger) => void,
		listUrl: string = ''
	) {
		this.userConfig = userConfig;
		this.onDelete = onDelete;
		this.onSave = onSave;
		this.listUrl = listUrl;

		this.dialog = new ReusableDialogState("Provisioning Template", 500);

		this.expressionFieldState = new ExpressionFieldState(ExpressionBuilderOutputType.Boolean, (updatedExpression) => {
			this.setTriggerExpression(updatedExpression);
		});

		this.completionTriggerActions = new TriggerActionsState(userConfig, (ids) => {
			this.OnCompletionTriggerActionIds = ids;
		}, listUrl);

		makeAutoObservable(this);
	}

	public setTemplateApprovalsListUrl(listUrl:string) {
		this.templateApprovalsListUrl = listUrl;
	}

	public setListUrl(listUrl: string) {
		this.listUrl = listUrl;
		this.completionTriggerActions.setListUrl(listUrl);
	}

	public setAvailableFields(listUrlColumns: IEntityColumn[]) {
		this.completionTriggerActions.setAvailableFields(listUrlColumns);
	}

	public setTokens(tokens:IEntityToken[]) {
		this.tokens = tokens;
		this.expressionFieldState.setAvailableTokens(tokens);
	}

	public setItem(item:ITemplateTrigger) {
		this.original = item;
		Object.assign(this, item);
		this.expressionFieldState.setExpression(item.TriggerExpression);
		this.completionTriggerActions.setSelected(item.OnCompletionTriggerActionIds || []);
	}
	
	public setTriggerExpression(val:string) {
		this.TriggerExpression = val;
	}

	public setTemplateUrl(val:string) {
		this.TemplateUrl = val;
		this.CachedTemplateFile = '';
	}

	public setTemplateProvisionIncludeSharePointIfTeams(val:boolean) {
		this.TemplateProvisionIncludeSharePointIfTeams = val;
		this.CachedTemplateFile = '';
	}

	public setTemplateProvisionIncludeFiles(val:boolean) {
		this.TemplateProvisionIncludeFiles = val;
		this.CachedTemplateFile = '';
	}

	public setTemplateProvisionIncludeListItems(val:boolean) {
		this.TemplateProvisionIncludeListItems = val;
		this.CachedTemplateFile = '';
	}

	public async ensureInitialLoaded() : Promise<void> {
		
	}
}

function TemplateTriggerEditor({
	state
}: {
	state: TemplateTriggerEditorState
}) {

	useEffect(() => {
		state.ensureInitialLoaded();
	}, [state]);

	return (

		<ReusableDialog state={state.dialog} footerButtons={state.dialogButtons}>
			
			<Stack>

				{state.saveClearTemplateMessage && <>

					<MessageBar messageBarType={MessageBarType.info} isMultiline={false}>
						The cached template file will be cleared when you save the changes.
					</MessageBar>
				</>}

				{state.CachedTemplateFile && <>

					<MessageBar messageBarType={MessageBarType.success} isMultiline={false} actions={<MessageBarButton onClick={() => state.clearTemplate()}>Clear prebuilt file</MessageBarButton>}>
						This template has been <a href={state.userConfig.tenantUrl + state.CachedTemplateFile}>prebuilt</a> for better performance. Clear it to pick up changes.
					</MessageBar>

				</>}

				<InfoTooltipLabel label='Template URL' title='URL to either a PnP template file or a content location to be copied during the provision process.'>
					<TextField value={state.TemplateUrl} onChange={(e, v) => state.setTemplateUrl(v || '')} />
				</InfoTooltipLabel>

				{state.TemplateApprovalListItemId ? <>

					<InfoTooltipLabel label='Template Approval Item' title={`This template is associated with a published template in the templates approval SharePoint List. The template will only be applied when the item is selected as a lookup in the main workspaces list.`}>
						<Link href={`${state.userConfig.tenantUrl}${state.templateApprovalsListUrl}/DispForm.aspx?ID=${state.TemplateApprovalListItemId}`} target='_blank'>View item</Link>
					</InfoTooltipLabel>

				</> : <>
					
					<InfoTooltipLabel label='Condition' title='Enter an expression to determine whether or not the template will be applied.'>

						<ExpressionField state={state.expressionFieldState} />

					</InfoTooltipLabel>


				</>}

				<Stack horizontal>

					{ state.isTeamsOrChannelUrl() && <>
						<InfoTooltipLabel label='Include SharePoint Site' title={`When syncing a Team or Private Channel, select this option to also include the associated SharePoint site template.`}>
							<Toggle checked={state.TemplateProvisionIncludeSharePointIfTeams} onText="On" offText="Off" onChange={(e, checked) => { state.setTemplateProvisionIncludeSharePointIfTeams(checked?.valueOf() ?? false); } } />
						</InfoTooltipLabel>
					</>}

					<InfoTooltipLabel label='Include Files' title={`When syncing SharePoint content, select this option to include the files.`}>
						<Toggle checked={state.TemplateProvisionIncludeFiles} onText="On" offText="Off" onChange={(e, checked) => { state.setTemplateProvisionIncludeFiles(checked?.valueOf() ?? false); } } />
					</InfoTooltipLabel>

					<InfoTooltipLabel label='Include List Items' title={`When syncing SharePoint content, select this option to include the list items.`}>
						<Toggle checked={state.TemplateProvisionIncludeListItems} onText="On" offText="Off" onChange={(e, checked) => { state.setTemplateProvisionIncludeListItems(checked?.valueOf() ?? false); } } />
					</InfoTooltipLabel>
				</Stack>

				<InfoTooltipLabel label='On Completion Actions' title='Configure some actions to execute when provisioning of this template has completed.'>
					<TriggerActions state={state.completionTriggerActions} />
				</InfoTooltipLabel>
				
			</Stack>
		</ReusableDialog>
	);
}

export default observer(TemplateTriggerEditor);
